/* The Settings row treatment, shared by every leaf in this workspace.

   An explicit import rather than four near-copies: a shadow root inherits no
   styling from its siblings, and `iconActions` in design/base.ts exists because
   that lesson was learned the expensive way once already (three copies, two icon
   sizes, one missing touch fallback). Workspace-local because nothing outside
   Settings has a grouped-list row.

   Rationale for the shapes here is ADR 0011; only the traps are repeated. */

import { css } from 'lit';

/**
 * `.row` — a control spanning the group, at least a thumb tall.
 * `.text` — a message where a row would be (the g-notice fallbacks).
 * `.sep`  — an inset rule above an action set apart from what it acts on.
 */
export const settingsRow = css`
  /* The gutter handover (ADR 0009) plus a constant text inset, in one expression
     that needs no breakpoint: 12px from the card edge on desktop, 28px from the
     screen edge on a phone. Everything on the page shares this left edge. */
  .row,
  .text {
    padding-left: var(--row-pad-l);
    padding-right: var(--row-pad-r);
  }
  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    /* ADR 0007 principle 8, and the reason the sweep's three undersized controls
       can't come back: a row cannot be shorter than a thumb. */
    min-height: var(--touch-min);
    padding-top: var(--space-2);
    padding-bottom: var(--space-2);
    font-size: 0.95rem;
  }
  .text {
    padding-top: var(--space-2);
    padding-bottom: var(--space-2);
  }
  /* Local <button> rather than g-button: a full-cell hit area is exactly what
     g-button's inline, padded, rounded inner button is not. */
  button.row {
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    font-family: var(--font-ui);
    text-align: left;
    cursor: pointer;
    transition: background-color 120ms ease;
  }
  button.row:active {
    background: var(--surface-2);
  }
  /* Gated: on touch a tap leaves a sticky :hover that only clears on the next tap
     elsewhere, so an unqualified rule leaves the row tinted after it is pressed.
     :active covers touch, above. */
  @media (hover: hover) {
    button.row:hover {
      background: var(--surface-2);
    }
  }
  button.row:focus-visible {
    outline: none;
    box-shadow: var(--ring);
  }
  /* Inset, and drawn with a pseudo-element because a border always spans the
     padding box — which is the full bleed being avoided (ADR 0011 decision 3). */
  .sep {
    position: relative;
  }
  .sep::before {
    content: '';
    position: absolute;
    top: 0;
    left: var(--row-pad-l);
    right: var(--row-pad-r);
    height: 1px;
    background: var(--border);
  }
`;

/* `splitBar` used to live here. It moved to design/base.ts when Map's detail
   sheet became its second workspace — see ADR 0012 decision 8. */
